import { useEffect, useMemo, useState } from "react";
import { View, Text, ScrollView, TextInput, TouchableOpacity, StyleSheet, Platform, ActivityIndicator, Image } from "react-native";
import { useRouter } from "expo-router";
import Head from "expo-router/head";
import { PublicNav } from "./PublicNav";

const NAVY = "#1B2A4A";
const NAVY_DARK = "#111C30";
const ORANGE = "#F5A623";
const WHITE = "#FFFFFF";
const TEXT = "#1F2937";
const API = "https://dashhound.app/api";

const sizeLabel = (s: string | null) =>
  s === "18_plus" ? "18″+" : s === "12_to_18" ? "12–18″" : s === "below_12" ? "under 12″" : "";
// Height divisions for breeds AKC ranks by size (All American Dog, Poodle, etc.).
const SIZE_PILLS: { key: string; label: string }[] = [
  { key: "below_12", label: 'Below 12"' },
  { key: "12_to_18", label: '12"–18"' },
  { key: "18_plus", label: '18"+' },
];
const yearLabel = (y: string) => (y === "lifetime" ? "All-Time" : y);
const isYr = (y?: string) => !!y && (/^\d{4}$/.test(y) || y === "lifetime");
const ordinal = (n: number) => {
  const s = ["th", "st", "nd", "rd"], v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};

type Row = { rank: number; primary: string; secondary: string; mph: number; publicSlug: string | null; breedSlug?: string; sizeClass?: string | null; photo?: string | null; callName?: string | null; photoFocusY?: number };

// Editor-picked featured dogs shown at the top of a breed page.
const FEATURED: Record<string, { name: string; reg: string; img: string; slug: string }[]> = {
  "Australian Cattle Dog": [
    { name: "Rhea", reg: "Large Marge Sent Me", img: "/featured/acd-rhea.jpg", slug: "large-marge-sent-me" },
    { name: "Teton", reg: "Wayout's More Attitude Than Altitude", img: "/featured/acd-teton.jpg", slug: "wayouts-more-attitude-than-altitude" },
  ],
  "Giant Schnauzer": [
    { name: "Rajah", reg: "Rajah", img: "/featured/gs-rajah.jpg", slug: "rajah" },
    { name: "Ripley", reg: "Dl's Believe It Or Not", img: "/featured/gs-ripley.jpg", slug: "dls-believe-it-or-not" },
  ],
  "Dachshund": [
    { name: "Finn", reg: "CoRoDach's Finnegan Begin Again", img: "/featured/dachshund-finn.jpg", slug: "corodachs-finnegan-begin-again" },
    { name: "Riot", reg: "CoRoDach's Organized Chaos", img: "/featured/dachshund-riot.jpg", slug: "corodachs-organized-chaos" },
  ],
};

// The sport's #1 dog — shown on the rankings home + the Greyhound breed page.
const HERO = {
  name: "Fritz",
  reg: "Fritsch FCAT6",
  slug: "fritsch",
  mph: "38.13",
  imgs: ["/featured/fritsch-1.jpg", "/featured/fritsch-2.jpg"],
};

// A single star dog featured with two photos on its breed page.
const SOLO_FEATURED: Record<string, { name: string; reg: string; slug: string; imgs: string[] }> = {
  "Siberian Husky": {
    name: "Poe",
    reg: "Taja's Swing Of The Pendulum",
    slug: "tajas-swing-of-the-pendulum",
    imgs: ["/featured/husky-poe-1.jpg", "/featured/husky-poe-2.jpg"],
  },
  "Beagle": {
    name: "Toast",
    reg: "KS Kneaded A Purebread",
    slug: "ks-kneaded-a-purebread",
    imgs: ["/featured/beagle-toast-1.jpg", "/featured/beagle-toast-2.jpg"],
  },
  "Shiba Inu": {
    name: "Vogue",
    reg: "Rexann's Strike A Pose",
    slug: "rexanns-strike-a-pose",
    imgs: ["/featured/shiba-vogue.jpg"],
  },
  "French Bulldog": {
    name: "Kash",
    reg: "Mr. Kash Money",
    slug: "mr-kash-money",
    imgs: ["/featured/fr-kash-1c.jpg", "/featured/fr-kash-2.jpg"],
  },
};

export function RankingsView({ segments }: { segments: string[] }) {
  const router = useRouter();
  const seg = (segments || []).filter(Boolean);

  // Parse the URL into view + breed slug + year
  let view: "breeds" | "dogs" | "breed" = "breeds";
  let slug = "";
  let urlYear: string | null = null;
  if (seg[0] === "fastest-dogs") { view = "dogs"; if (isYr(seg[1])) urlYear = seg[1]; }
  else if (seg[0] === "breed") { view = "breed"; slug = seg[1] || ""; if (isYr(seg[2])) urlYear = seg[2]; }
  else if (isYr(seg[0])) { view = "breeds"; urlYear = seg[0]; }

  const [years, setYears] = useState<string[]>([]);
  const [latest, setLatest] = useState("2026");
  const [breedList, setBreedList] = useState<string[]>([]);
  const [rows, setRows] = useState<Row[]>([]);
  const [breedName, setBreedName] = useState<string>("");
  const [top, setTop] = useState<Row | null>(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [sizeClass, setSizeClass] = useState("12_to_18");
  const [story, setStory] = useState("");

  // Breeds AKC ranks by size (rows carry a sizeClass); each division has its own #1.
  const breedHasSizes = view === "breed" && rows.some((r) => !!r.sizeClass);
  // If the selected division has no dogs for this breed, fall back to one that does.
  useEffect(() => {
    if (!breedHasSizes) return;
    const present = SIZE_PILLS.map((s) => s.key).filter((k) => rows.some((r) => r.sizeClass === k));
    if (present.length && !present.includes(sizeClass)) {
      setSizeClass(present.includes("12_to_18") ? "12_to_18" : present[0]);
    }
  }, [rows, breedHasSizes, sizeClass]);

  // Each featured dog's MOST RECENT ranking (rank + year) — so a featured dog with
  // no current-year runs still shows its latest standing (e.g. "13th · 2025").
  const [featRanks, setFeatRanks] = useState<Record<string, { rank: number; year: string }>>({});
  useEffect(() => {
    const slugs: string[] = [];
    if (view === "breed" && breedName) {
      (FEATURED[breedName] || []).forEach((d) => slugs.push(d.slug));
      if (SOLO_FEATURED[breedName]) slugs.push(SOLO_FEATURED[breedName].slug);
    }
    if (!slugs.length) { setFeatRanks({}); return; }
    let cancelled = false;
    Promise.all(slugs.map((s) =>
      fetch(`${API}/rankings/dog-latest?slug=${encodeURIComponent(s)}`)
        .then((r) => r.json()).then((d) => [s, d] as const).catch(() => [s, null] as const)
    )).then((entries) => {
      if (cancelled) return;
      const m: Record<string, { rank: number; year: string }> = {};
      for (const [s, d] of entries) if (d && d.rank) m[s] = { rank: d.rank, year: String(d.year) };
      setFeatRanks(m);
    });
    return () => { cancelled = true; };
  }, [view, breedName]);

  const year = urlYear || latest;
  // Featured rank label: "3rd Ranked" for the current year, "13th · 2025" otherwise.
  const rankLabelFor = (slug: string) => {
    const fr = featRanks[slug];
    if (!fr) return "";
    return fr.year === latest ? `${ordinal(fr.rank)} Ranked` : `${ordinal(fr.rank)} · ${fr.year}`;
  };
  const suffix = (y: string) => (y && y !== latest ? `/${y}` : "");
  const uBreeds = (y: string) => `/(auth)/fast-cat-rankings${suffix(y)}`;
  const uDogs = (y: string) => `/(auth)/fast-cat-rankings/fastest-dogs${suffix(y)}`;
  const uBreed = (s: string, y: string) => `/(auth)/fast-cat-rankings/breed/${s}${suffix(y)}`;
  const go = (path: string) => router.push(path as any);
  const goProfile = (s: string) => router.push(`/dog/${s}` as any);

  useEffect(() => {
    fetch(`${API}/rankings/years`).then((r) => r.json())
      .then((ys: string[]) => { setYears(ys); setLatest(ys.find((y) => y !== "lifetime") || "2026"); })
      .catch(() => { setYears(["lifetime", "2026", "2025", "2024"]); setLatest("2026"); });
    fetch(`${API}/rankings/breeds`).then((r) => r.json()).then(setBreedList).catch(() => setBreedList([]));
  }, []);

  useEffect(() => {
    setLoading(true); setRows([]); setTop(null); setStory("");
    const done = (rs: Row[]) => { setRows(rs); setTop(rs[0] || null); setLoading(false); };
    if (view === "dogs") {
      fetch(`${API}/rankings/fastest-dogs?year=${year}`).then((r) => r.json())
        .then((d) => { setStory(d.story || ""); done((d.dogs || []).map((x: any) => ({ rank: x.rank, primary: x.dogName, secondary: x.breed, mph: x.speed, publicSlug: x.publicSlug, sizeClass: x.sizeClass, photo: x.photo, callName: x.callName, photoFocusY: x.photoFocusY }))); })
        .catch(() => setLoading(false));
    } else if (view === "breed") {
      fetch(`${API}/rankings/breed-detail?slug=${encodeURIComponent(slug)}&year=${year}`).then((r) => r.json())
        .then((d) => { setBreedName(d.breed || ""); setStory(d.story || ""); done((d.dogs || []).map((x: any) => ({ rank: x.rank, primary: x.dogName, secondary: x.owner && x.owner !== "Dog" ? x.owner : "", mph: x.speed, publicSlug: x.publicSlug, sizeClass: x.sizeClass, photo: x.photo, callName: x.callName, photoFocusY: x.photoFocusY }))); })
        .catch(() => setLoading(false));
    } else {
      fetch(`${API}/rankings/breed-speeds?year=${year}`).then((r) => r.json())
        .then((d) => { setStory(d.story || ""); done((d.breeds || []).map((x: any) => ({ rank: x.rank, primary: x.breed, secondary: x.dogName, mph: x.speed, publicSlug: x.publicSlug, breedSlug: slugify(x.breed), sizeClass: x.sizeClass }))); })
        .catch(() => setLoading(false));
    }
  }, [view, slug, year]);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    let out = rows;
    if (view === "breed" && rows.some((r) => !!r.sizeClass)) {
      out = out.filter((r) => r.sizeClass === sizeClass);
    }
    if (t && view === "breeds") out = out.filter((r) => r.primary.toLowerCase().includes(t));
    return out;
  }, [rows, q, view, sizeClass]);

  // Titles + intro copy (keywords), per view.
  const yl = yearLabel(year);
  let h1 = "", sub = "", intro = "", metaTitle = "", metaDesc = "";
  if (view === "dogs") {
    h1 = `The Fastest Fast CAT Dogs${year !== "lifetime" ? ` · ${yl}` : ""}`;
    sub = "Every breed, ranked by speed.";
    intro = `The fastest dogs in AKC Fast CAT for ${yl}, ranked by MPH across all breeds and mixed breeds.${top ? ` The single fastest is ${top.primary} — a ${top.secondary} — at ${top.mph.toFixed(2)} mph.` : ""}`;
    metaTitle = `Fastest Fast CAT Dogs (${yl}) — Every Breed Ranked by Speed`;
    metaDesc = intro;
  } else if (view === "breed") {
    const b = breedName || "dog";
    h1 = `Fastest ${b}s${year !== "lifetime" ? ` · ${yl}` : " · All-Time"}`;
    sub = "Fast CAT speed rankings.";
    intro = `How fast is a ${b}? These are the fastest ${b}s in AKC Fast CAT for ${yl}, ranked by speed in the 100-yard dash.${top ? ` The fastest ${b} on record for ${yl} is ${top.primary} at ${top.mph.toFixed(2)} mph.` : ""} Browse by year and see where your ${b} would place.`;
    metaTitle = `Fastest ${b}s — Fast CAT Speed Rankings ${yl}`;
    metaDesc = intro;
  } else {
    h1 = `Fastest Dog Breeds by Fast CAT Speed${year !== "lifetime" ? ` · ${yl}` : ""}`;
    sub = "Ranked by each breed's fastest dog.";
    intro = `Every AKC breed and mixed breed ranked by its fastest dog's Fast CAT speed for ${yl}. Because of the height handicap, small dogs hold their own against the sighthounds. Tap any breed for its full rankings.`;
    metaTitle = `Fastest Dog Breeds by Fast CAT Speed (${yl})`;
    metaDesc = intro;
  }

  return (
    <View style={{ flex: 1, backgroundColor: WHITE }}>
      {Platform.OS === "web" && (
        <Head>
          <title>{`${metaTitle} — Dash-Hound`}</title>
          <meta name="description" content={metaDesc} />
          <meta property="og:title" content={`${metaTitle} — Dash-Hound`} />
          <meta property="og:description" content={metaDesc} />
          <meta property="og:image" content="https://dashhound.app/pages/fast-cat-rankings.jpg" />
          <meta property="og:type" content="website" />
        </Head>
      )}

      <PublicNav />

      <ScrollView contentContainerStyle={styles.content}>
        <View style={styles.hero}>
          <Text style={styles.heroKicker}>RANKINGS</Text>
          <Text style={styles.heroTitle}>{h1}</Text>
          <Text style={styles.heroSub}>{sub}</Text>
        </View>

        {/* Fastest dog in the sport — rankings home + Greyhound page */}
        {view === "breeds" || (view === "breed" && breedName === "Greyhound") ? (
          <View style={styles.featWrap}>
            <Text style={styles.featKicker}>FASTEST DOG IN THE SPORT</Text>
            <Text style={styles.heroFeatName}>
              {HERO.name} — {HERO.reg}
            </Text>
            <Text style={styles.heroFeatSub}>
              The #1 fastest dog in AKC Fast CAT · {HERO.mph} mph
            </Text>
            <View style={styles.featRow}>
              {HERO.imgs.map((src, i) => (
                <TouchableOpacity key={i} style={styles.featCard} activeOpacity={0.85} onPress={() => goProfile(HERO.slug)}>
                  {Platform.OS === "web" ? (
                    // @ts-ignore
                    <img src={src} alt={`${HERO.name} (${HERO.reg}) — fastest Fast CAT dog`} style={{ width: "100%", height: 230, objectFit: "cover", objectPosition: "center 30%", display: "block" } as any} />
                  ) : (
                    <View style={{ width: "100%", height: 230, backgroundColor: "#F3F4F6" }}>
                      <Image source={{ uri: `https://dashhound.app${src}` }} style={{ width: "100%", height: 230 }} resizeMode="cover" />
                    </View>
                  )}
                </TouchableOpacity>
              ))}
            </View>
            <TouchableOpacity onPress={() => goProfile(HERO.slug)} activeOpacity={0.7}>
              <Text style={styles.heroFeatLink}>See {HERO.name}'s profile →</Text>
            </TouchableOpacity>
          </View>
        ) : null}

        {/* Featured dogs (breed pages that have picks) */}
        {view === "breed" && breedName && FEATURED[breedName] ? (
          <View style={styles.featWrap}>
            <Text style={styles.featKicker}>FEATURED {breedName.toUpperCase()}S</Text>
            <View style={styles.featRow}>
              {FEATURED[breedName].map((d) => {
                const rankLabel = rankLabelFor(d.slug);
                return (
                <TouchableOpacity key={d.slug} style={styles.featCard} activeOpacity={0.85} onPress={() => goProfile(d.slug)}>
                  {Platform.OS === "web" ? (
                    // @ts-ignore
                    <img src={d.img} alt={`${d.name} — ${d.reg}`} style={{ width: "100%", height: 210, objectFit: "cover", display: "block" } as any} />
                  ) : (
                    <View style={{ width: "100%", height: 210, backgroundColor: "#F3F4F6" }}>
                      <Image source={{ uri: `https://dashhound.app${d.img}` }} style={{ width: "100%", height: 210 }} resizeMode="cover" />
                    </View>
                  )}
                  <View style={styles.featCap}>
                    <Text style={styles.featName}>
                      {d.name}{rankLabel ? <Text style={styles.featRank}>  ·  {rankLabel}</Text> : null}
                    </Text>
                    <Text style={styles.featReg} numberOfLines={2}>"{d.reg}"</Text>
                    <Text style={styles.featLink}>See profile →</Text>
                  </View>
                </TouchableOpacity>
                );
              })}
            </View>
          </View>
        ) : null}

        {/* Single star dog with two photos (breed pages) */}
        {view === "breed" && breedName && SOLO_FEATURED[breedName] ? (() => {
          const s = SOLO_FEATURED[breedName];
          const rankLabel = rankLabelFor(s.slug);
          return (
            <View style={styles.featWrap}>
              <Text style={styles.featKicker}>FEATURED {breedName.toUpperCase()}</Text>
              <Text style={styles.heroFeatName}>
                {s.name}{rankLabel ? <Text style={styles.featRank}>  ·  {rankLabel}</Text> : null}
              </Text>
              <Text style={styles.heroFeatSub}>"{s.reg}"</Text>
              <View style={styles.featRow}>
                {s.imgs.map((src, i) => (
                  <TouchableOpacity key={i} style={styles.featCard} activeOpacity={0.85} onPress={() => goProfile(s.slug)}>
                    {Platform.OS === "web" ? (
                      // @ts-ignore
                      <img src={src} alt={`${s.name} (${s.reg})`} style={{ width: "100%", height: 230, objectFit: "cover", objectPosition: "center 30%", display: "block" } as any} />
                    ) : (
                      <View style={{ width: "100%", height: 230, backgroundColor: "#F3F4F6" }}>
                        <Image source={{ uri: `https://dashhound.app${src}` }} style={{ width: "100%", height: 230 }} resizeMode="cover" />
                      </View>
                    )}
                  </TouchableOpacity>
                ))}
              </View>
              <TouchableOpacity onPress={() => goProfile(s.slug)} activeOpacity={0.7}>
                <Text style={styles.heroFeatLink}>See {s.name}'s profile →</Text>
              </TouchableOpacity>
            </View>
          );
        })() : null}

        {/* Intro keyword copy */}
        <View style={styles.introWrap}><Text style={styles.introText}>{story || intro}</Text></View>

        {/* View tabs */}
        <View style={styles.controls}>
          <View style={styles.tabs}>
            <TouchableOpacity style={[styles.tab, view === "breeds" && styles.tabActive]} onPress={() => go(uBreeds(year))}>
              <Text style={[styles.tabText, view === "breeds" && styles.tabTextActive]}>Fastest by Breed</Text>
            </TouchableOpacity>
            <TouchableOpacity style={[styles.tab, view === "dogs" && styles.tabActive]} onPress={() => go(uDogs(year))}>
              <Text style={[styles.tabText, view === "dogs" && styles.tabTextActive]}>Fastest Dogs</Text>
            </TouchableOpacity>
          </View>

          {/* Breed picker */}
          {Platform.OS === "web" ? (
            // @ts-ignore raw select
            <select value={view === "breed" ? breedName : ""} onChange={(e: any) => { const b = e.target.value; if (b) go(uBreed(slugify(b), year)); else go(uBreeds(year)); }}
              style={{ padding: "12px 14px", borderRadius: 10, border: "1px solid #E5E7EB", fontSize: 15, color: NAVY, background: WHITE, width: "100%", fontWeight: 600 } as any}>
              <option value="">Pick a breed…</option>
              {breedList.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          ) : view === "breed" ? (
            <TouchableOpacity style={styles.backBreed} onPress={() => go(uBreeds(year))}><Text style={styles.backBreedText}>← All Breeds</Text></TouchableOpacity>
          ) : null}

          {/* Year pills — WRAP so they never run off the page */}
          <View style={styles.yearRow}>
            {years.map((y) => {
              const target = view === "dogs" ? uDogs(y) : view === "breed" ? uBreed(slug, y) : uBreeds(y);
              const active = y === year;
              return (
                <TouchableOpacity key={y} onPress={() => go(target)} style={[styles.yearBtn, active && styles.yearBtnActive]}>
                  <Text style={[styles.yearBtnText, active && styles.yearBtnTextActive]}>{yearLabel(y)}</Text>
                </TouchableOpacity>
              );
            })}
          </View>

          {/* Height divisions — for breeds AKC ranks by size */}
          {breedHasSizes ? (
            <View>
              <Text style={styles.sizeLabel}>Height division</Text>
              <View style={styles.sizeRow}>
                {SIZE_PILLS.filter((sp) => rows.some((r) => r.sizeClass === sp.key)).map((sp) => {
                  const active = sp.key === sizeClass;
                  return (
                    <TouchableOpacity key={sp.key} onPress={() => setSizeClass(sp.key)} style={[styles.sizeBtn, active && styles.sizeBtnActive]}>
                      <Text style={[styles.sizeBtnText, active && styles.sizeBtnTextActive]}>{sp.label}</Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
            </View>
          ) : null}

          {view === "breeds" && (
            <TextInput value={q} onChangeText={setQ} placeholder="Search a breed…" placeholderTextColor="#9CA3AF" style={styles.search} />
          )}
        </View>

        {/* Table */}
        <View style={styles.tableWrap}>
          <View style={styles.tableHead}>
            <Text style={[styles.th, styles.colRank]}>#</Text>
            <Text style={[styles.th, styles.colMain]}>{view === "breeds" ? "Breed" : "Dog"}</Text>
            <Text style={[styles.th, styles.colMph]}>{view === "breeds" ? "Top MPH" : "MPH"}</Text>
          </View>
          {loading ? (
            <View style={styles.loading}><ActivityIndicator color={NAVY} /></View>
          ) : filtered.length === 0 ? (
            <Text style={styles.empty}>No results for {yl}.</Text>
          ) : (
            filtered.map((r) => {
              // A ranked dog that has a public Dash-Hound profile — highlight it.
              const isDash = view !== "breeds" && !!r.publicSlug;
              const inner = (
                <>
                  <Text style={[styles.td, styles.colRank, r.rank <= 3 && styles.rankTop]}>{r.rank}</Text>
                  {isDash ? (
                    r.photo ? (
                      Platform.OS === "web" ? (
                        // @ts-ignore
                        <img src={r.photo} alt={r.callName || r.primary} style={{ width: 42, height: 42, borderRadius: 21, objectFit: "cover", objectPosition: `center ${r.photoFocusY ?? 50}%`, border: `2px solid ${ORANGE}`, marginRight: 10, flexShrink: 0, display: "block" } as any} />
                      ) : (
                        <Image source={{ uri: r.photo }} style={{ width: 42, height: 42, borderRadius: 21, borderWidth: 2, borderColor: ORANGE, marginRight: 10 }} />
                      )
                    ) : (
                      <View style={styles.avatarPaw}><Text style={styles.avatarPawText}>🐾</Text></View>
                    )
                  ) : null}
                  <View style={styles.colMain}>
                    {view === "breeds" ? (
                      <>
                        <Text style={styles.mainName}>{r.primary}{r.sizeClass ? <Text style={styles.sizeTag}>  {sizeLabel(r.sizeClass)}</Text> : null}</Text>
                        {r.secondary ? <Text style={styles.sub} numberOfLines={1}>Led by {r.secondary}</Text> : null}
                        <TouchableOpacity onPress={() => go(uBreed(r.breedSlug!, year))} style={styles.seeBtn}>
                          <Text style={styles.seeBtnText}>See breed rankings →</Text>
                        </TouchableOpacity>
                      </>
                    ) : isDash ? (
                      <>
                        <View style={styles.dashNameRow}>
                          <Text style={styles.dashName}>{r.callName || r.primary}</Text>
                          <View style={styles.dashBadge}><Text style={styles.dashBadgeText}>🐾 On Dash-Hound</Text></View>
                        </View>
                        <Text style={styles.dashReg} numberOfLines={1}>{r.primary}{r.sizeClass ? `  ${sizeLabel(r.sizeClass)}` : ""}</Text>
                      </>
                    ) : (
                      <>
                        <Text style={styles.mainName}>{r.primary}{r.sizeClass ? <Text style={styles.sizeTag}>  {sizeLabel(r.sizeClass)}</Text> : null}</Text>
                        {r.secondary ? (
                          view === "dogs"
                            ? <TouchableOpacity onPress={() => go(uBreed(slugify(r.secondary), year))}><Text style={styles.subLink}>{r.secondary}</Text></TouchableOpacity>
                            : <Text style={styles.sub}>{r.secondary}</Text>
                        ) : null}
                      </>
                    )}
                  </View>
                  <Text style={[styles.td, styles.colMph, styles.mph, isDash && styles.mphDash]}>{r.mph.toFixed(2)}</Text>
                </>
              );
              return isDash ? (
                <TouchableOpacity key={`${r.rank}-${r.primary}`} activeOpacity={0.85} onPress={() => goProfile(r.publicSlug!)} style={[styles.tr, styles.trDash]}>
                  {inner}
                </TouchableOpacity>
              ) : (
                <View key={`${r.rank}-${r.primary}`} style={[styles.tr, r.rank <= 3 && styles.trTop]}>
                  {inner}
                </View>
              );
            })
          )}
        </View>

        <View style={styles.cta}>
          <Text style={styles.ctaTitle}>Where would your dog land?</Text>
          <Text style={styles.ctaText}>Log your runs in Dash-Hound to see your dog's speed, points, projected breed rank, and titles — calculated automatically.</Text>
          <View style={{ flexDirection: "row", gap: 10, marginTop: 16, flexWrap: "wrap" }}>
            <TouchableOpacity style={styles.ctaPrimary} onPress={() => router.push("/(auth)/what-is-fast-cat/" as any)}><Text style={styles.ctaPrimaryText}>What Is Fast CAT? →</Text></TouchableOpacity>
            <TouchableOpacity style={styles.ctaSecondary} onPress={() => router.push("/(auth)/tools/" as any)}><Text style={styles.ctaSecondaryText}>Speed Calculator</Text></TouchableOpacity>
          </View>
        </View>
        <View style={styles.footer}><Text style={styles.footerText}>© 2026 Dash-Hound LLC. All rights reserved.</Text></View>
      </ScrollView>
    </View>
  );
}

function slugify(b: string) { return b.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, ""); }

const styles = StyleSheet.create({
  content: { flexGrow: 1 },
  hero: { backgroundColor: NAVY, paddingHorizontal: 24, paddingTop: 40, paddingBottom: 24, alignItems: "center" },
  heroKicker: { color: ORANGE, fontSize: 12, fontWeight: "800", letterSpacing: 1.5, marginBottom: 10 },
  heroTitle: { color: WHITE, fontSize: 30, fontWeight: "900", textAlign: "center", letterSpacing: -0.5, maxWidth: 760, lineHeight: 38 },
  heroSub: { color: "rgba(255,255,255,0.7)", fontSize: 15, textAlign: "center", marginTop: 8 },
  introWrap: { maxWidth: 720, width: "100%", alignSelf: "center", paddingHorizontal: 20, paddingTop: 20 },
  introText: { fontSize: 15, color: "#4B5563", lineHeight: 24 },
  featWrap: { maxWidth: 720, width: "100%", alignSelf: "center", paddingHorizontal: 20, paddingTop: 22 },
  featKicker: { color: ORANGE, fontSize: 12, fontWeight: "800", letterSpacing: 1, marginBottom: 12 },
  heroFeatName: { fontSize: 22, fontWeight: "900", color: NAVY, marginTop: -6, marginBottom: 2 },
  heroFeatSub: { fontSize: 14, color: "#4B5563", fontWeight: "600", marginBottom: 14 },
  heroFeatLink: { fontSize: 15, color: "#B45309", fontWeight: "800", marginTop: 14 },
  featRow: { flexDirection: "row", flexWrap: "wrap", gap: 16 },
  featCard: {
    flexGrow: 1, flexBasis: "45%", minWidth: 280, borderRadius: 14, overflow: "hidden",
    borderWidth: 1, borderColor: "#E5E7EB", backgroundColor: WHITE,
    ...(Platform.OS === "web" ? ({ boxShadow: "0 4px 16px rgba(27,42,74,0.10)", cursor: "pointer" } as any) : { shadowColor: NAVY, shadowOpacity: 0.1, shadowRadius: 10, shadowOffset: { width: 0, height: 4 }, elevation: 4 }),
  },
  featCap: { padding: 14 },
  featName: { fontSize: 18, fontWeight: "900", color: NAVY },
  featReg: { fontSize: 13, fontStyle: "italic", color: "#6B7280", marginTop: 2 },
  featRank: { fontSize: 14, fontStyle: "normal", color: "#B45309", fontWeight: "800" },
  featLink: { fontSize: 13, color: "#B45309", fontWeight: "800", marginTop: 8 },
  controls: { maxWidth: 720, width: "100%", alignSelf: "center", paddingHorizontal: 16, paddingTop: 16, gap: 12 },
  tabs: { flexDirection: "row", gap: 8 },
  tab: { flex: 1, paddingVertical: 10, borderRadius: 10, backgroundColor: "#F3F4F6", borderWidth: 1, borderColor: "#E5E7EB", alignItems: "center" },
  tabActive: { backgroundColor: NAVY, borderColor: NAVY },
  tabText: { color: NAVY, fontWeight: "800", fontSize: 14 },
  tabTextActive: { color: WHITE },
  backBreed: { alignSelf: "flex-start", paddingVertical: 6 },
  backBreedText: { color: "#B45309", fontWeight: "700", fontSize: 15 },
  sizeLabel: { fontSize: 12, fontWeight: "800", color: "#6B7280", letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 6 },
  sizeRow: { flexDirection: "row", flexWrap: "wrap", gap: 8 },
  sizeBtn: { paddingHorizontal: 14, paddingVertical: 8, borderRadius: 10, backgroundColor: "#F3F4F6", borderWidth: 1, borderColor: "#E5E7EB" },
  sizeBtnActive: { backgroundColor: NAVY, borderColor: NAVY },
  sizeBtnText: { color: NAVY, fontWeight: "700", fontSize: 13 },
  sizeBtnTextActive: { color: WHITE },
  yearRow: { flexDirection: "row", flexWrap: "wrap", gap: 8 },
  yearBtn: { paddingHorizontal: 14, paddingVertical: 7, borderRadius: 18, backgroundColor: "#F3F4F6", borderWidth: 1, borderColor: "#E5E7EB" },
  yearBtnActive: { backgroundColor: ORANGE, borderColor: ORANGE },
  yearBtnText: { color: NAVY, fontWeight: "700", fontSize: 13 },
  yearBtnTextActive: { color: NAVY },
  search: { borderWidth: 1, borderColor: "#E5E7EB", borderRadius: 10, paddingHorizontal: 14, paddingVertical: 12, fontSize: 15, color: TEXT, backgroundColor: WHITE, ...(Platform.OS === "web" ? ({ outlineStyle: "none" } as any) : {}) },
  tableWrap: { maxWidth: 720, width: "100%", alignSelf: "center", paddingHorizontal: 16, paddingTop: 16 },
  tableHead: { flexDirection: "row", paddingVertical: 10, paddingHorizontal: 12, borderBottomWidth: 2, borderBottomColor: "#E5E7EB" },
  th: { fontSize: 12, fontWeight: "800", color: "#6B7280", textTransform: "uppercase", letterSpacing: 0.5 },
  tr: { flexDirection: "row", alignItems: "center", paddingVertical: 12, paddingHorizontal: 12, borderBottomWidth: 1, borderBottomColor: "#F3F4F6" },
  trTop: { backgroundColor: "#FFFBEB" },
  trDash: { backgroundColor: "#FFF4E5", borderLeftWidth: 4, borderLeftColor: ORANGE, ...(Platform.OS === "web" ? ({ cursor: "pointer" } as any) : {}) },
  dashNameRow: { flexDirection: "row", alignItems: "center", flexWrap: "wrap", gap: 6 },
  dashName: { fontSize: 16, fontWeight: "900", color: NAVY },
  dashBadge: { backgroundColor: ORANGE, borderRadius: 9, paddingHorizontal: 7, paddingVertical: 2 },
  dashBadgeText: { color: NAVY, fontSize: 10, fontWeight: "800", letterSpacing: 0.3 },
  dashReg: { fontSize: 12, color: "#8A6D3B", fontStyle: "italic", marginTop: 1 },
  mphDash: { color: "#B45309" },
  avatarPaw: { width: 42, height: 42, borderRadius: 21, borderWidth: 2, borderColor: ORANGE, backgroundColor: "#FFF4E5", alignItems: "center", justifyContent: "center", marginRight: 10 },
  avatarPawText: { fontSize: 20 },
  seeBtn: { alignSelf: "flex-start", marginTop: 6, borderWidth: 1, borderColor: ORANGE, borderRadius: 8, paddingHorizontal: 10, paddingVertical: 4, ...(Platform.OS === "web" ? ({ cursor: "pointer" } as any) : {}) },
  seeBtnText: { color: "#B45309", fontWeight: "800", fontSize: 12 },
  td: { fontSize: 15, color: TEXT },
  colRank: { width: 44 },
  colMain: { flex: 1, paddingRight: 8 },
  colMph: { width: 84, textAlign: "right" },
  rankTop: { color: ORANGE, fontWeight: "900" },
  mainName: { fontSize: 16, fontWeight: "700", color: NAVY },
  mainLink: { fontSize: 16, fontWeight: "700", color: NAVY, ...(Platform.OS === "web" ? ({ cursor: "pointer" } as any) : {}) },
  sizeTag: { fontSize: 12, fontWeight: "600", color: "#9CA3AF" },
  sub: { fontSize: 16, color: "#374151", marginTop: 3, fontWeight: "600" },
  subLink: { fontSize: 16, color: "#374151", marginTop: 3, fontWeight: "600", ...(Platform.OS === "web" ? ({ cursor: "pointer" } as any) : {}) },
  profileLink: { fontSize: 12, color: "#B45309", fontWeight: "700", marginTop: 4 },
  mph: { fontWeight: "800", color: NAVY, fontSize: 16 },
  loading: { paddingVertical: 40, alignItems: "center" },
  empty: { paddingVertical: 40, textAlign: "center", color: "#6B7280" },
  cta: { maxWidth: 720, width: "100%", alignSelf: "center", marginTop: 32, marginHorizontal: 16, padding: 22, backgroundColor: "#F9FAFB", borderRadius: 14, borderWidth: 1, borderColor: "#E5E7EB" },
  ctaTitle: { fontSize: 20, fontWeight: "800", color: NAVY, marginBottom: 8 },
  ctaText: { fontSize: 15, color: TEXT, lineHeight: 22 },
  ctaPrimary: { backgroundColor: NAVY, paddingHorizontal: 18, paddingVertical: 12, borderRadius: 8 },
  ctaPrimaryText: { color: WHITE, fontWeight: "700", fontSize: 14 },
  ctaSecondary: { backgroundColor: ORANGE, paddingHorizontal: 18, paddingVertical: 12, borderRadius: 8 },
  ctaSecondaryText: { color: NAVY, fontWeight: "800", fontSize: 14 },
  footer: { backgroundColor: NAVY_DARK, paddingVertical: 32, alignItems: "center", marginTop: 40 },
  footerText: { color: "rgba(255,255,255,0.35)", fontSize: 13 },
});
